'use client'

import { FilteredList } from 'design-system'
import { Check } from 'lucide-react'
import { cn } from 'design-system'
import { FC, useEffect, useRef } from 'react'

import { SearchFilterConfig } from './types'

type SearchSubFilterProps = {
  config: SearchFilterConfig
  value: string[]
  onChange: (value: string[]) => void
}

export const SearchSubFilter: FC<SearchSubFilterProps> = ({
  config,
  value,
  onChange
}) => {
  // The search input resets on unmount (popover closed / Back), so clear the
  // consumer's server-side query too; otherwise the next open shows the old
  // query's results under an empty input. Read through a ref so a new
  // onSearch identity (it changes whenever the consumer's items do) doesn't
  // trigger the reset mid-search.
  const onSearchRef = useRef(config.onSearch)
  useEffect(() => {
    onSearchRef.current = config.onSearch
  })
  useEffect(() => {
    const ref = onSearchRef
    return () => ref.current?.('')
  }, [])

  const toggleItem = (item: { id: string }) => {
    const next = value.includes(item.id)
      ? value.filter(v => v !== item.id)
      : [...value, item.id]
    onChange(next)
  }

  const renderItemWithCheckbox = (item: { id: string }) => {
    const checked = value.includes(item.id)
    return (
      <div className="flex items-center gap-x-2">
        <span
          className={cn(
            'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
            checked
              ? 'border-blue-600 bg-blue-600'
              : 'border-slate-300 bg-white'
          )}
          aria-hidden>
          {checked && <Check size={12} className="text-white" />}
        </span>
        {config.renderItem(item)}
      </div>
    )
  }

  return (
    <FilteredList
      items={config.items}
      loading={config.loading}
      error={config.error}
      onSelect={toggleItem}
      filterPredicate={config.filterPredicate}
      placeholder={config.placeholder ?? `Search ${config.label.toLowerCase()}`}
      renderItem={renderItemWithCheckbox}
      itemsName={config.itemsName ?? config.label.toLowerCase()}
      onSearch={config.onSearch}
      fetchMore={config.fetchMore}
      ariaLabel={config.label}
    />
  )
}
